// src/components/CaseStudies.tsx
import React from "react";
import { motion } from "framer-motion";
import DataOps from "../assets/DataOps.jpg";
import MLOps from "../assets/MLOps.jpg";
import FinanceTransformation from "../assets/FinanceTransformaion.jpg";
import Data_Ops from "../assets/DataOps.png";
import ML_ops from "../assets/MLops.png";
import finance_transformation from "../assets/financeTransformation.png";
import arrowRight from "../assets/ArrowRight.png";
import { useNavigate } from "react-router-dom";

interface TechLogo {
  src: string;
  width?: string;
  height?: string;
}

interface CaseStudyCardProps {
  image: string;
  title: string;
  description: string;
  techLogos: TechLogo[];
  id: string;
}

const CaseStudyCard: React.FC<CaseStudyCardProps> = ({
  image,
  title,
  description,
  techLogos,
  id,
}) => {
  const navigate = useNavigate(); // Initialize navigate

  const handleReadMore = () => {
    navigate(`/case-studies/${id}`); // Navigate to case study detail page
  };

  return (
    <div className="flex-shrink-0 flex flex-col items-start mx-2 sm:mx-4 md:mx-6 cursor-pointer relative w-full max-w-[580px]">
      {/* Top Image */}
      <img
        className="self-stretch h-[250px] sm:h-[300px] md:h-[350px] w-full rounded-t-xl shadow-md border border-gray-200 object-cover"
        src={image}
        alt={title}
      />

      {/* Card Content */}
      <div className="self-stretch w-full px-4 sm:px-6 py-4 sm:py-6 rounded-b-xl flex flex-col gap-4 sm:gap-6 md:gap-8 bg-white border border-slate-300 shadow-md min-h-[400px] sm:min-h-[450px] md:min-h-[500px]">
        <div className="flex flex-col gap-3 sm:gap-4 md:gap-5">
          <h3 className="text-lg sm:text-xl md:text-2xl w-full max-w-[350px] font-semibold font-['IBM Plex Sans Condensed'] text-gray-800">
            {title}
          </h3>
          <p className="self-stretch justify-start text-gray-700 text-base sm:text-lg md:text-xl font-medium font-['Nunito Sans'] leading-relaxed tracking-tight">
            {description}
          </p>

          {/* Tech Stack */}
          <div className="px-2 py-2 my-2 sm:my-3 md:my-4 bg-sky-100 inline-flex justify-start items-center w-fit">
            <span className="text-blue-900 text-sm sm:text-base md:text-xl font-semibold font-['Nunito Sans'] leading-relaxed tracking-tight">
              TECH STACK
            </span>
          </div>

          {/* Logos */}
          <div className="flex flex-wrap gap-3 sm:gap-4 md:gap-6">
            {techLogos.map((logo, idx) => (
              <div
                key={idx}
                className="h-8 sm:h-9 md:h-10 p-1 my-2 sm:my-3 md:my-5 bg-white rounded-md flex justify-center items-center"
              >
                <img
                  src={logo.src}
                  alt="tech logo"
                  className="w-full max-w-[300px] sm:max-w-[400px] md:max-w-[480px] h-[80px] sm:h-[100px] md:h-[120px] my-2 sm:my-3 md:my-4 object-contain"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Read More */}
        <div className="inline-flex items-center gap-2 sm:gap-3 md:gap-4 mt-auto">
          <button
            onClick={handleReadMore}
            className="py-2 rounded-lg text-base sm:text-lg md:text-xl font-semibold font-['Nunito Sans'] text-blue-900 hover:text-blue-700 transition-colors">
            Read in detail
          </button>
          <img src={arrowRight} alt="" className="w-[18px] sm:w-[27px] md:w-[30px] h-[10px] sm:h-[11px] md:h-[12px]" />
        </div>
      </div>
    </div>
  );
};

const CaseStudies: React.FC = () => {
  const cards = [
    {
      id: "dataops",
      image: DataOps,
      title: "DataOps & Managed Services Framework",
      description: "Achieved a 40% reduction in incident turnaround time, increased platform usage with automated monitoring, and a global DataOps team",
      techLogos: [{ src: Data_Ops }],
    },
    {
      id: "mlops",
      image: MLOps,
      title: "MLOps-Enabled Databricks Lakehouse Design",
      description: "Onboarded 10+ use cases in 12 months & reduced pipeline runtimes by 40% through automated deployments and a modern Azure data platform",
      techLogos: [{ src: ML_ops }],
    },
    {
      id: "finance-transformation",
      image: FinanceTransformation,
      title: "Finance Transformation for a Pharma Leader",
      description: "Eliminated 90% of manual interventions and shortened reporting turnaround time by 50%, enabling Day 1 financial book closure with robust, automated workflows",
      techLogos: [{ src: finance_transformation }],
    },
  ];

  // Duplicate cards for seamless looping
  const marqueeCards = [...cards, ...cards];

  return (
    <div className="relative w-full overflow-hidden bg-gray-50 py-8 sm:py-10 md:py-12">
      <div className="px-4 sm:px-8 md:px-16 lg:px-24 xl:px-[100px] pb-6 sm:pb-8 md:pb-[50px] self-stretch inline-flex flex-col justify-start items-center gap-4 sm:gap-5 md:gap-6">
        <div className="w-full max-w-[940px] text-center justify-start text-zinc-700 text-2xl sm:text-3xl md:text-4xl font-semibold font-['IBM Plex Sans'] tracking-wide">
          Proven Excellence: Real Analytics, Real Impact
        </div>
        <div className="self-stretch text-center justify-start text-gray-700 text-base sm:text-lg md:text-xl font-normal font-['Nunito Sans'] leading-relaxed tracking-tight">
          Senpiper's analytics platforms consistently deliver robust performance, enterprise-grade reliability, and measurable business results. Each case study demonstrates the effectiveness of our technology
        </div>
      </div>
      <motion.div
        className="flex"
        animate={{ x: ["0%", "-100%"] }}
        transition={{
          ease: "linear",
          duration: 20,
          repeat: Infinity,
        }}
      >
        {marqueeCards.map((card, idx) => (
          <CaseStudyCard key={idx} {...card} />
        ))}
      </motion.div>
    </div>
  );
};

export default CaseStudies;
